// 部分書式（inline style run）を CSS 値へ解決する共有ヘルパー。
//
// 編集 DOM（markdown-wysiwyg.ts）・非編集表示（MarkdownTextView）・PDF 出力
// （canvas-pdf-export.ts）が同じ解決規則を使い、「画面と出力で部分書式の見た目が違う」状態を
// 構造的に防ぐ。

import {
  isNonEmptyTextInlineStyle,
  markdownInlineDecorationSignature,
  type MarkdownInlineDecoration,
  type TextInlineStyle,
} from "@refinement-canvas/domain";
import { resolveTextBackground } from "./text-color";
import { isValidHexColor, normalizeHexColor } from "./hex-color";

/** インラインコードの左右 padding（em）。DOM（`.canvas-inline-code--start/--end`）と Canvas 出力で共有する。 */
export const INLINE_CODE_PADDING_EM = 0.25;

/**
 * インラインコードの背景色。`styles.css` の明テーマ `--color-surface-muted` の解決値。
 * DOM は CSS 変数を、PDF・サムネイル（Canvas 2D。常に明テーマで描く）は本定数を使う。
 * 値の一致は text-inline-style-css.test.ts が styles.css を読んで固定する。
 */
export const INLINE_CODE_BACKGROUND_COLOR = "#eef0f6";

/** インラインコードの基底クラス（等幅フォントと背景）。 */
export const INLINE_CODE_CLASS = "canvas-inline-code";

export interface TextInlineStyleCss {
  readonly color?: string | undefined;
  readonly backgroundColor?: string | undefined;
  readonly fontSize?: string | undefined;
  readonly fontWeight?: string | undefined;
  readonly fontStyle?: string | undefined;
  readonly textDecorationLine?: string | undefined;
}

export interface TextInlineStyleCssOptions {
  /**
   * 見出し行の中か。見出しは行スタイルとして太字になっており、その太字は Markdown の
   * 意味上の装飾なので、部分書式の `bold: false` で打ち消さない（基底の太字だけを打ち消す）。
   */
  readonly headingLine?: boolean | undefined;
  /** 記法から導かれる装飾。run に bold の明示が無いときだけ、記法の太字を採用する。 */
  readonly decoration?: MarkdownInlineDecoration | undefined;
}

/**
 * 表本文 run の直下に実際に現れる面を解決する。
 * 明示 fill、インラインコード面、行面の順で選び、一般の style 解決規則は変更しない。
 */
export function resolveTextInlineEffectiveBackground(
  style: TextInlineStyle,
  decoration: MarkdownInlineDecoration | undefined,
  rowBackground: string
): string {
  return (
    resolveTextBackground(style.fill) ??
    (decoration?.code === true ? INLINE_CODE_BACKGROUND_COLOR : rowBackground)
  );
}

/**
 * run の override を CSS 値へ解決する。上書きしない項目はキーごと省略し、
 * 呼び出し側の既定（基底書式・行スタイル）を継承させる。
 */
export function textInlineStyleCss(
  style: TextInlineStyle,
  options?: TextInlineStyleCssOptions | undefined
): TextInlineStyleCss {
  const css: {
    color?: string | undefined;
    backgroundColor?: string | undefined;
    fontSize?: string | undefined;
    fontWeight?: string | undefined;
    fontStyle?: string | undefined;
    textDecorationLine?: string | undefined;
  } = {};
  if (style.color !== undefined && isValidHexColor(style.color)) {
    css.color = normalizeHexColor(style.color);
  }
  // 背景は選択文字の glyph box にだけ付くインラインハイライト。hex 以外（背景なし）は
  // 塗らず、図形全体の基底背景が下から見える状態を保つ。
  const background = resolveTextBackground(style.fill);
  if (background !== undefined) css.backgroundColor = background;
  if (style.fontSize !== undefined) css.fontSize = `${style.fontSize}px`;
  // 太字: run の明示（true/false）が最優先。明示が無ければ記法の結果を使う。
  if (style.bold === true) css.fontWeight = "700";
  else if (style.bold === false && options?.headingLine !== true) css.fontWeight = "400";
  else if (options?.decoration?.bold === true) css.fontWeight = "700";
  // 斜体・取消線は run に対応項目が無いため、記法の結果をそのまま反映する。
  if (options?.decoration?.italic === true) css.fontStyle = "italic";
  if (options?.decoration?.strikethrough === true) css.textDecorationLine = "line-through";
  return css;
}

/**
 * 装飾から CSS クラス名を返す（現在はインラインコードのみ）。装飾なしは undefined。
 *
 * クラス名を `textInlineStyleCss` の戻り値へ混ぜてはいけない。戻り値は呼び出し側が
 * `style={... as CSSProperties}` として spread しており、`className` を混ぜると不正な
 * CSS プロパティとして DOM へ渡ることになるためである。
 *
 * コードスパンは部分書式の境界で複数の span に分かれ得るため、padding と角丸を当てる
 * `--start` / `--end` は論理コード範囲の先頭・末尾の span にだけ付ける
 * （DOM と Canvas で padding の回数をそろえる）。
 */
export function textInlineDecorationClassName(
  decoration: MarkdownInlineDecoration | undefined,
  options?: { readonly codeStart?: boolean | undefined; readonly codeEnd?: boolean | undefined }
): string | undefined {
  if (decoration?.code !== true) return undefined;
  return [
    INLINE_CODE_CLASS,
    ...(options?.codeStart === true ? [`${INLINE_CODE_CLASS}--start`] : []),
    ...(options?.codeEnd === true ? [`${INLINE_CODE_CLASS}--end`] : []),
  ].join(" ");
}

/**
 * run の override を、DOM の構造比較に使える安定した署名へ変換する。
 * 編集 DOM の正準性判定（editorNeedsRebuild）が「期待する span 構成と一致するか」を
 * 文字列比較だけで判定できるようにする。
 *
 * 上書きが 1 項目も無い区間は空文字列を返す。呼び出し側はこれを「span で包まない素の
 * テキストノード」の印として使うため、空 style でも非空の署名を返してはいけない
 * （返すと、書式の付いていない本文まで span へ包まれて正準形が崩れる）。
 */
export function textInlineStyleSignature(style: TextInlineStyle): string {
  if (!isNonEmptyTextInlineStyle(style)) return "";
  return [
    style.color ?? "",
    style.fill ?? "",
    style.fontSize === undefined ? "" : String(style.fontSize),
    style.bold === undefined ? "" : style.bold ? "1" : "0",
  ].join("|");
}

/**
 * 部分書式と記法の装飾をまとめた署名。編集 DOM の正準性判定に使う。
 * 両方が空のときだけ空文字列（span で包まない素のテキストノード）を返す。
 */
export function textInlinePieceSignature(
  style: TextInlineStyle,
  decoration: MarkdownInlineDecoration
): string {
  const styleSignature = textInlineStyleSignature(style);
  const decorationSignature = markdownInlineDecorationSignature(decoration);
  if (styleSignature === "" && decorationSignature === "") return "";
  return `${styleSignature}#${decorationSignature}`;
}
